<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import axios from "axios";
import Swal from "sweetalert2";

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export default {
  page: {
    title: "Import Blueprint",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
  },

  data() {
    return {
      title: "Import Blueprint",
      items: [
        { text: "Tools Asesmen", href: "/master-asesmen/tools-asesmen" },
        { text: "Import Blueprint", active: true },
      ],

      // Multiple file state
      selectedFiles: [],
      isDragOver: false,

      // Upload state
      isUploading: false,
      uploadProgress: 0,

      // Validation
      fileError: null,
      MAX_FILE_SIZE_MB: MAX_FILE_SIZE_MB,

      // Result state
      importResult: null,
    };
  },

  computed: {
    totalFilesCount() {
      return this.selectedFiles.length;
    },

    totalSizeDisplay() {
      if (this.selectedFiles.length === 0) return "0 KB";
      const totalBytes = this.selectedFiles.reduce((acc, f) => acc + f.size, 0);
      const kb = totalBytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    isReadyToUpload() {
      return this.selectedFiles.length > 0 && !this.isUploading;
    },
  },

  methods: {
    formatFileSize(bytes) {
      if (!bytes) return "0 KB";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    // ─── File Selection ───────────────────────────────────────────────────────

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileInputChange(event) {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) this.handleFilesSelected(files);
      event.target.value = "";
    },

    onDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length > 0) this.handleFilesSelected(files);
    },

    handleFilesSelected(files) {
      this.fileError = null;
      let newFiles = [];
      let rejected = [];

      files.forEach((file) => {
        const error = this.validateFile(file);
        if (error) {
          rejected.push(`${file.name}: ${error}`);
        } else {
          // Hindari duplikasi file dengan nama dan ukuran yang sama
          const exists = this.selectedFiles.some(
            (f) => f.name === file.name && f.size === file.size
          );
          if (!exists) {
            newFiles.push(file);
          }
        }
      });

      if (rejected.length > 0) {
        this.fileError = rejected.join(" | ");
      }

      this.selectedFiles = [...this.selectedFiles, ...newFiles];
    },

    // ─── Validation ───────────────────────────────────────────────────────────

    validateFile(file) {
      if (!file) return "File kosong.";

      const extension = "." + file.name.split(".").pop().toLowerCase();
      const isValidExtension = ALLOWED_EXTENSIONS.includes(extension);
      const isValidMime =
        ALLOWED_MIME_TYPES.includes(file.type) || file.type === "";

      if (!isValidExtension || !isValidMime) {
        return `Format file tidak didukung (${ALLOWED_EXTENSIONS.join(", ")}).`;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `Ukuran file melebihi ${MAX_FILE_SIZE_MB}MB.`;
      }

      return null;
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
      if (this.selectedFiles.length === 0) {
        this.fileError = null;
      }
    },

    clearAllFiles() {
      this.selectedFiles = [];
      this.fileError = null;
      this.importResult = null;
    },

    // ─── Upload Process ───────────────────────────────────────────────────────

    async uploadFiles() {
      if (!this.isReadyToUpload) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.importResult = null;

      const formData = new FormData();
      this.selectedFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      try {
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_BACKEND_URL_VERSION + "import-excel/blueprint",
          data: formData,
          headers: {
            Authorization: `Bearer ${localStorage.access_token}`,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          },
        });

        const resData = response.data;
        const resStats = resData?.data || {};

        const totalBerhasil = resStats.total_berhasil || 0;
        const totalGagal = resStats.total_gagal || 0;
        const isSuccess = totalGagal === 0 && totalBerhasil > 0;

        this.importResult = {
          success: isSuccess,
          message: resStats.message || resData?.message || "Bulk import blueprint selesai.",
          total_files: resStats.total_files || this.selectedFiles.length,
          total_berhasil: totalBerhasil,
          total_gagal: totalGagal,
          total_soal_diupdate: resStats.total_soal_diupdate || 0,
          details: resStats.details || [],
        };

        Swal.fire({
          icon: isSuccess ? "success" : totalBerhasil > 0 ? "warning" : "error",
          title: isSuccess ? "Import Berhasil!" : "Import Selesai dengan Catatan",
          html: `${this.importResult.message}<br><small class="text-muted">${totalBerhasil} berhasil, ${totalGagal} gagal.</small>`,
          confirmButtonColor: isSuccess ? "#34c38f" : "#f1b44c",
        });
      } catch (error) {
        const resData = error.response?.data;
        const resStats = resData?.data || {};

        let errorMessage =
          resData?.meta?.message ||
          resData?.message ||
          "Terjadi kesalahan sistem saat melakukan bulk import blueprint.";

        this.importResult = {
          success: false,
          message: errorMessage,
          total_files: resStats.total_files || this.selectedFiles.length,
          total_berhasil: resStats.total_berhasil || 0,
          total_gagal: resStats.total_gagal || this.selectedFiles.length,
          total_soal_diupdate: resStats.total_soal_diupdate || 0,
          details: resStats.details || [],
        };

        Swal.fire({
          icon: "error",
          title: "Import Gagal",
          text: errorMessage,
          confirmButtonColor: "#f46a6a",
        });
      } finally {
        this.isUploading = false;
      }
    },

    goBack() {
      this.$router.push("/master-asesmen/tools-asesmen");
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <h5 class="mb-0">
              <i class="fa fa-file-import me-2 text-primary"></i>
              Bulk Import Blueprint dari Excel
            </h5>
            <button class="btn btn-secondary btn-sm" @click="goBack">
              <i class="fa fa-arrow-left me-1"></i> Kembali
            </button>
          </div>

          <div class="card-body">
            <!-- ─── PANDUAN IMPORT ────────────────────────────────────────────── -->
            <div class="alert alert-info d-flex align-items-start gap-3 mb-4">
              <i class="fa fa-circle-info fa-lg mt-1"></i>
              <div>
                <strong>Panduan Bulk Import Blueprint</strong>
                <ul class="mb-1 mt-1 ps-3">
                  <li>
                    Dapat mengunggah <strong>beberapa file Excel sekaligus</strong> secara bersamaan.
                  </li>
                  <li>
                    Sistem langsung membaca <code>Kode Kompetensi</code> dan <code>Standar Kompetensi</code> dari masing-masing file untuk mencocokkan data.
                  </li>
                  <li>
                    Format file yang didukung: <code>.xlsx</code>, <code>.xls</code> (Maks. {{ MAX_FILE_SIZE_MB }}MB per file).
                  </li>
                </ul>
              </div>
            </div>

            <!-- ─── DROP ZONE MULTI-FILE ──────────────────────────────────────── -->
            <div
              class="drop-zone"
              :class="{
                'drop-zone--active': isDragOver,
                'drop-zone--uploading': isUploading,
              }"
              @click="!isUploading && triggerFileInput()"
              @dragover="onDragOver"
              @dragleave="onDragLeave"
              @drop="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                multiple
                accept=".xlsx,.xls"
                class="d-none"
                @change="onFileInputChange"
                :disabled="isUploading"
              />

              <div class="drop-zone__content">
                <i
                  class="fa fa-cloud-arrow-up drop-zone__icon"
                  :class="isDragOver ? 'text-primary' : 'text-muted'"
                ></i>
                <p class="drop-zone__text mb-1">
                  <span v-if="isDragOver">Lepaskan file-file di sini</span>
                  <span v-else>
                    <strong>Klik untuk pilih file</strong> atau seret & lepas beberapa file ke sini
                  </span>
                </p>
                <p class="text-muted small mb-0">
                  Dapat memilih lebih dari satu file Excel (.xlsx, .xls)
                </p>
              </div>
            </div>

            <!-- ─── FILE ERROR ALERT ──────────────────────────────────────────── -->
            <div
              v-if="fileError"
              class="alert alert-danger mt-3 d-flex align-items-center gap-2"
            >
              <i class="fa fa-circle-exclamation"></i>
              <span>{{ fileError }}</span>
            </div>

            <!-- ─── PROGRESS BAR ──────────────────────────────────────────────── -->
            <div v-if="isUploading" class="mt-4">
              <div class="d-flex justify-content-between mb-1">
                <span class="small fw-semibold">Mengunggah dan memproses file...</span>
                <span class="small text-muted">{{ uploadProgress }}%</span>
              </div>
              <div class="progress" style="height: 10px">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  :style="{ width: uploadProgress + '%' }"
                ></div>
              </div>
            </div>

            <!-- ─── DAFTAR FILE TERPILIH ──────────────────────────────────────── -->
            <div v-if="selectedFiles.length > 0 && !isUploading" class="mt-4">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0 fw-bold">
                  <i class="fa fa-folder-open me-1 text-primary"></i>
                  File Terpilih ({{ totalFilesCount }} file - Total: {{ totalSizeDisplay }})
                </h6>
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="triggerFileInput"
                  >
                    <i class="fa fa-plus me-1"></i> Tambah File
                  </button>
                  <button
                    class="btn btn-outline-danger btn-sm"
                    @click="clearAllFiles"
                  >
                    <i class="fa fa-trash me-1"></i> Bersihkan
                  </button>
                  <button
                    class="btn btn-primary btn-sm fw-semibold"
                    @click="uploadFiles"
                  >
                    <i class="fa fa-upload me-1"></i> Mulai Bulk Import ({{ totalFilesCount }})
                  </button>
                </div>
              </div>

              <div class="table-responsive border rounded" style="max-height: 250px">
                <table class="table table-sm table-hover mb-0 align-middle">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th style="width: 40px" class="text-center">#</th>
                      <th>Nama File</th>
                      <th style="width: 120px">Ukuran</th>
                      <th style="width: 80px" class="text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(file, idx) in selectedFiles" :key="idx">
                      <td class="text-center text-muted small">{{ idx + 1 }}</td>
                      <td>
                        <i class="fa fa-file-excel text-success me-2"></i>
                        <span class="fw-semibold">{{ file.name }}</span>
                      </td>
                      <td class="text-muted small">
                        {{ formatFileSize(file.size) }}
                      </td>
                      <td class="text-center">
                        <button
                          class="btn btn-link text-danger p-0"
                          title="Hapus file ini"
                          @click="removeFile(idx)"
                        >
                          <i class="fa fa-xmark"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- ─── HASIL REKAPITULASI BULK IMPORT ────────────────────────────── -->
            <div v-if="importResult" class="mt-4">
              <div
                class="alert d-flex align-items-start gap-2 mb-3"
                :class="
                  importResult.success ? 'alert-success' : 'alert-warning'
                "
              >
                <i
                  class="fa mt-1"
                  :class="
                    importResult.success
                      ? 'fa-circle-check text-success'
                      : 'fa-triangle-exclamation text-warning'
                  "
                ></i>
                <div class="flex-grow-1">
                  <strong>{{
                    importResult.success ? "Proses Selesai (Sukses)" : "Proses Selesai dengan Catatan"
                  }}</strong>
                  <p class="mb-0">{{ importResult.message }}</p>

                  <div class="d-flex gap-2 mt-2 flex-wrap">
                    <span class="badge bg-primary">
                      Total File: {{ importResult.total_files || 0 }}
                    </span>
                    <span class="badge bg-success">
                      File Berhasil: {{ importResult.total_berhasil || 0 }}
                    </span>
                    <span
                      v-if="(importResult.total_gagal || 0) > 0"
                      class="badge bg-danger"
                    >
                      File Gagal: {{ importResult.total_gagal }}
                    </span>
                    <span class="badge bg-info">
                      Total Soal Diupdate: {{ importResult.total_soal_diupdate || 0 }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Rincian Tabel File Hasil Import -->
              <div
                v-if="importResult.details && importResult.details.length > 0"
                class="card border"
              >
                <div class="card-header bg-light py-2">
                  <h6 class="mb-0 fw-bold">
                    <i class="fa fa-list-check me-1 text-primary"></i>
                    Rincian Hasil Import Tiap File
                  </h6>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-sm table-striped mb-0 align-middle">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 40px" class="text-center">#</th>
                          <th>Nama File</th>
                          <th>Kode Kompetensi</th>
                          <th>Nama Kompetensi</th>
                          <th class="text-center" style="width: 80px">Level</th>
                          <th class="text-center" style="width: 100px">Total Soal</th>
                          <th class="text-center" style="width: 100px">Status</th>
                          <th>Keterangan</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, idx) in importResult.details"
                          :key="idx"
                        >
                          <td class="text-center text-muted small">{{ idx + 1 }}</td>
                          <td class="fw-semibold">
                            <i class="fa fa-file-excel text-success me-1"></i>
                            {{ item.file_name }}
                          </td>
                          <td>
                            <code>{{ item.kode_kompetensi || '-' }}</code>
                          </td>
                          <td class="small">{{ item.nama_kompetensi || '-' }}</td>
                          <td class="text-center">
                            <span v-if="item.level" class="badge bg-secondary">
                              Lv {{ item.level }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-info">
                              {{ item.total_sukses || 0 }} / {{ item.total_soal || 0 }}
                            </span>
                          </td>
                          <td class="text-center">
                            <span
                              class="badge"
                              :class="item.status ? 'bg-success' : 'bg-danger'"
                            >
                              {{ item.status ? 'Berhasil' : 'Gagal' }}
                            </span>
                          </td>
                          <td class="small">
                            <span :class="item.status ? 'text-success' : 'text-danger'">
                              {{ item.message }}
                            </span>
                            <!-- Tampilkan rincian error baris jika ada -->
                            <div
                              v-if="item.errors && item.errors.length > 0"
                              class="text-danger small mt-1"
                            >
                              <div
                                v-for="(err, eIdx) in item.errors.slice(0, 3)"
                                :key="eIdx"
                              >
                                &bull; {{ err.row ? `Baris ${err.row}: ` : '' }}{{ err.message }}
                              </div>
                              <div v-if="item.errors.length > 3" class="text-muted fst-italic">
                                ... dan {{ item.errors.length - 3 }} kesalahan lainnya
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: #f8fafc;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #6f42c1;
  background-color: #f3f0fc;
}

.drop-zone--uploading {
  cursor: not-allowed;
  opacity: 0.7;
}

.drop-zone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.drop-zone__icon {
  font-size: 2.5rem;
  margin-bottom: 12px;
}
</style>
